import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import React, { useState } from 'react';
import { toast } from 'sonner';
import { reportsApi } from '@/api/reports';
import { Eye, Spinner, Edit2, Trash } from '@/components/icons';
import { DeleteIllustration } from '@/components/modals/DeleteIllustration';
import { BlankCardImage, TemplateCardImage } from '@/components/modals/TemplateCardImage';
import FeatureUnavailableChip from '@/components/subscription/FeatureUnavailableChip';
import { AppDialogLayout, Text } from '@/components/system';
import { Button } from '@/components/ui/button';
import { Card, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useFeatureEntitlement } from '@/hooks/use-feature-entitlement';
import { getEntitlementBlockedMessage } from '@/lib/entitlements';
import { cn } from '@/lib/utils';
import { ReportTemplate } from '@/types/report';

interface TemplateSelectionModalProps {
    /**
     * Header title for the template list. Owned by this component rather than the
     * modal shell so the edit and delete branches can replace it with their own,
     * as every other wizard-style dialog does.
     */
    title: string;
    templates: ReportTemplate[];
    onSelect: (template: ReportTemplate) => void;
    onPreview: (template: ReportTemplate) => void;
    onSkip: () => void;
    showBlankOption?: boolean;
}

export const TemplateSelectionModal: React.FC<TemplateSelectionModalProps> = ({
    title,
    templates,
    onSelect,
    onPreview,
    onSkip,
    showBlankOption = true,
}) => {
    const queryClient = useQueryClient();
    const [localTemplates, setLocalTemplates] = useState(templates);
    const [templateToDelete, setTemplateToDelete] = useState<ReportTemplate | null>(null);
    const [templateToEdit, setTemplateToEdit] = useState<ReportTemplate | null>(null);
    const [editName, setEditName] = useState('');
    const [editDescription, setEditDescription] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);
    const [isUpdatingMetadata, setIsUpdatingMetadata] = useState(false);
    const platformTemplateAccess = useFeatureEntitlement('platformTemplates.use');

    const userTemplates = localTemplates.filter((template) => template.source === 'user');
    const catalogTemplates = localTemplates.filter((template) => template.source === 'laudopsi');

    const handleDelete = async () => {
        if (!templateToDelete) return;

        try {
            setIsDeleting(true);
            await reportsApi.deleteTemplate(templateToDelete.id);
            setLocalTemplates((prev) =>
                prev.filter((template) => template.id !== templateToDelete.id),
            );
            toast.success('Modelo excluído com sucesso');
            setTemplateToDelete(null);
        } catch (error) {
            console.error('Failed to delete template', error);
            toast.error('Erro ao excluir modelo');
        } finally {
            setIsDeleting(false);
        }
    };

    const openEditTemplate = (template: ReportTemplate) => {
        setTemplateToDelete(null);
        setTemplateToEdit(template);
        setEditName(template.name);
        setEditDescription(template.description || '');
    };

    const handleUpdateMetadata = async (event?: React.FormEvent<HTMLFormElement>) => {
        event?.preventDefault();
        if (!templateToEdit || isUpdatingMetadata) return;

        const trimmedName = editName.trim();
        const currentDescription = templateToEdit.description || '';

        if (!trimmedName) return;
        if (trimmedName === templateToEdit.name && editDescription === currentDescription) {
            return;
        }

        try {
            setIsUpdatingMetadata(true);
            const updatedTemplate = await reportsApi.updateTemplateMetadata(templateToEdit.id, {
                name: trimmedName,
                description: editDescription,
            });

            setLocalTemplates((prev) =>
                prev.map((template) =>
                    template.id === updatedTemplate.id ? updatedTemplate : template,
                ),
            );
            queryClient.setQueryData<ReportTemplate[]>(
                ['report-templates'],
                (current) =>
                    current?.map((template) =>
                        template.id === updatedTemplate.id ? updatedTemplate : template,
                    ) ?? current,
            );
            toast.success('Template atualizado com sucesso.');
            setTemplateToEdit(null);
        } catch (error) {
            console.error('Failed to update template metadata', error);

            if (isAxiosError(error) && error.response?.status === 409) {
                toast.error('Já existe um modelo com este nome.');
            } else {
                toast.error('Erro ao atualizar template.');
            }
        } finally {
            setIsUpdatingMetadata(false);
        }
    };

    const handleSelectTemplate = (template: ReportTemplate) => {
        if (template.source === 'laudopsi' && platformTemplateAccess.shouldBlock) {
            toast.warning(getEntitlementBlockedMessage(platformTemplateAccess.decision));
            return;
        }

        onSelect(template);
    };

    const renderTemplateCard = (template: ReportTemplate) => {
        const isCatalogBlocked =
            template.source === 'laudopsi' && platformTemplateAccess.shouldBlock;
        const canEditTemplate = template.source === 'user' && !template.read_only;
        const showUpgradeChip =
            isCatalogBlocked &&
            platformTemplateAccess.isUnavailableInPlan &&
            !!platformTemplateAccess.availableFromPlan;

        return (
            <Card
                key={template.id}
                className={cn(
                    'group relative flex cursor-pointer flex-col border-border bg-card p-3 transition-all hover:border-border-strong hover:shadow-md',
                    isCatalogBlocked && 'cursor-default',
                )}
                onClick={() => handleSelectTemplate(template)}
            >
                <TemplateCardImage template={template} />

                <div className="min-w-0">
                    <CardTitle className="line-clamp-2 text-base font-semibold leading-tight text-foreground">
                        {template.name}
                    </CardTitle>
                    {showUpgradeChip && platformTemplateAccess.availableFromPlan ? (
                        <div className="mt-2">
                            <FeatureUnavailableChip
                                availablePlan={platformTemplateAccess.availableFromPlan}
                            />
                        </div>
                    ) : null}
                </div>

                {/* Floated over the plate so the artwork keeps the full card width;
                    still hover/focus-revealed, as in the previous row layout. */}
                <div className="absolute right-3 top-3 z-10 flex shrink-0 items-center gap-1 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
                    <Button
                        variant="outline"
                        className="size-8 border-border bg-card/90 p-0 text-muted-foreground backdrop-blur-sm hover:border-border-strong hover:bg-surface hover:text-foreground"
                        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                            event.stopPropagation();
                            onPreview(template);
                        }}
                        title="Visualizar"
                        aria-label={`Visualizar ${template.name}`}
                    >
                        <Eye className="size-4" />
                    </Button>
                    {canEditTemplate && (
                        <Button
                            variant="outline"
                            className="size-8 border-border bg-card/90 p-0 text-muted-foreground backdrop-blur-sm hover:border-border-strong hover:bg-surface hover:text-foreground"
                            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                event.stopPropagation();
                                openEditTemplate(template);
                            }}
                            title="Editar detalhes"
                            aria-label={`Editar detalhes de ${template.name}`}
                        >
                            <Edit2 className="size-4" />
                        </Button>
                    )}
                    {template.can_delete && (
                        <Button
                            variant="outline"
                            className="size-8 border-border bg-card/90 p-0 text-muted-foreground backdrop-blur-sm hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                event.stopPropagation();
                                setTemplateToEdit(null);
                                setTemplateToDelete(template);
                            }}
                            title="Excluir modelo"
                            aria-label={`Excluir ${template.name}`}
                        >
                            <Trash className="size-4" />
                        </Button>
                    )}
                </div>
            </Card>
        );
    };

    /**
     * One grid for everything. The user's own templates and the LaudoPsi
     * catalog used to be separate labelled sections; they now share a single
     * flow, led by the catalog so a psychologist with no templates of their own
     * still meets a ready-made starting point before their own list.
     */
    const renderTemplateGrid = () => {
        const items = [...catalogTemplates, ...userTemplates];

        if (items.length === 0 && !showBlankOption) return null;

        return (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {showBlankOption && renderBlankCard()}
                {items.map(renderTemplateCard)}
            </div>
        );
    };

    const renderBlankCard = () => (
        <Card
            className="group flex cursor-pointer flex-col border-dashed border-border-strong bg-card p-3 transition-all hover:border-border-strong hover:shadow-md"
            onClick={onSkip}
        >
            <BlankCardImage />

            <CardTitle className="text-base font-semibold leading-tight text-foreground">
                Começar do zero
            </CardTitle>
        </Card>
    );

    /**
     * Editing and deleting are views branching off the list, not steps forward in
     * it: each replaces the list's title in the header and is left through the
     * header's back control, never a footer `Cancelar`. See the modal rules in
     * `docs/design-system.md`.
     */
    if (templateToEdit) {
        const trimmedName = editName.trim();
        const currentDescription = templateToEdit.description || '';
        const hasChanges =
            trimmedName !== templateToEdit.name || editDescription !== currentDescription;

        return (
            <AppDialogLayout
                title="Editar detalhes do template"
                backAction={{ label: 'Voltar', onClick: () => setTemplateToEdit(null) }}
                footer={
                    <div className="flex justify-end">
                        <Button
                            type="button"
                            onClick={() => void handleUpdateMetadata()}
                            disabled={!trimmedName || !hasChanges || isUpdatingMetadata}
                        >
                            {isUpdatingMetadata ? (
                                <>
                                    <Spinner className="animate-spin" />
                                    Salvando...
                                </>
                            ) : (
                                'Salvar alterações'
                            )}
                        </Button>
                    </div>
                }
            >
                {/* The form stays in the body so Enter still saves; the footer sits
                    outside it and goes through the same handler. */}
                <form onSubmit={handleUpdateMetadata} className="space-y-4">
                    <div className="space-y-2">
                        <label
                            htmlFor="edit-template-name"
                            className="text-sm font-medium text-foreground"
                        >
                            Nome do Modelo <span className="text-destructive">*</span>
                        </label>
                        <Input
                            id="edit-template-name"
                            value={editName}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                setEditName(event.target.value)
                            }
                            placeholder="Ex: Avaliação TDAH Adulto"
                            autoFocus
                        />
                    </div>

                    <div className="space-y-2">
                        <label
                            htmlFor="edit-template-description"
                            className="text-sm font-medium text-foreground"
                        >
                            Descrição
                        </label>
                        <Textarea
                            id="edit-template-description"
                            value={editDescription}
                            onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                                setEditDescription(event.target.value)
                            }
                            placeholder="Descreva brevemente o propósito deste modelo..."
                            rows={3}
                        />
                    </div>
                </form>
            </AppDialogLayout>
        );
    }

    if (templateToDelete) {
        return (
            <AppDialogLayout
                title={`Excluir modelo “${templateToDelete.name}”?`}
                backAction={{ label: 'Voltar', onClick: () => setTemplateToDelete(null) }}
                footer={
                    <div className="flex justify-end">
                        <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
                            {isDeleting ? (
                                <>
                                    <Spinner className="animate-spin" />
                                    Excluindo...
                                </>
                            ) : (
                                'Excluir'
                            )}
                        </Button>
                    </div>
                }
            >
                <div className="space-y-4">
                    <DeleteIllustration />
                    <Text variant="description">
                        Tem certeza que deseja excluir este modelo? Esta ação não pode ser desfeita
                        e o modelo será removido permanentemente.
                    </Text>
                </div>
            </AppDialogLayout>
        );
    }

    return <AppDialogLayout title={title}>{renderTemplateGrid()}</AppDialogLayout>;
};
